import { useEvent } from '@shared-core/hooks/use-event'
import { useEffect } from 'react'

type PollerEntry = { owners: number; generation: number; timer?: ReturnType<typeof setTimeout> }

// Один цикл на ключ, сколько бы компонентов его ни использовали: запросы не дублируются.
const pollers = new Map<string, PollerEntry>()

type SharedPollerOptions = {
  key: string
  enabled: boolean
  intervalMs: number
  // Возвращает задержку до следующего запуска в мс; undefined означает intervalMs.
  run: () => Promise<number | undefined>
}

export function useSharedPoller({ key, enabled, intervalMs, run }: SharedPollerOptions) {
  const runStable = useEvent(run)
  const getIntervalMs = useEvent(() => intervalMs)

  useEffect(() => {
    if (!enabled) return

    let entry = pollers.get(key)

    if (!entry) {
      entry = { owners: 0, generation: 0 }
      pollers.set(key, entry)
    }

    const current = entry
    current.owners += 1

    if (current.owners === 1) {
      current.generation += 1
      const generation = current.generation

      const tick = async () => {
        let nextDelayMs: number | undefined

        try {
          nextDelayMs = await runStable()
        } catch {
          // Временный сбой не останавливает опрос: следующий запуск идёт по интервалу.
        }

        if (generation !== current.generation || current.owners === 0) return
        current.timer = setTimeout(() => void tick(), nextDelayMs ?? getIntervalMs())
      }

      void tick()
    }

    return () => {
      current.owners -= 1

      if (current.owners > 0) return

      clearTimeout(current.timer)
      current.timer = undefined
      current.generation += 1
    }
  }, [enabled, key, runStable, getIntervalMs])
}
